import { useState } from 'react';

import { Avatar, Stack, Typography } from '@mui/material';

import { Icon } from '@/components/atoms/Icon/Icon';
import { Logo } from '@/components/atoms/Logo/Logo';
import { layout } from '@/theme/layout';
import { AccountSheet } from './AccountSheet';

export interface OnboardingAppBarProps {
  /** The section being answered. Omit to show the logo. */
  label?: string;
  /** Omit to show a person icon until the name is known. */
  initials?: string;
  /** Named in the settings sheet, so a wrong account is visible. */
  email?: string;
  onSignOut: () => void;
  onHelp: () => void;
}

/**
 * OnboardingAppBar names the current section, or shows the logo, and puts the
 * avatar that opens the settings sheet (Help, Sign out) on every onboarding
 * screen.
 */
export const OnboardingAppBar = ({
  label,
  initials,
  email,
  onSignOut,
  onHelp,
}: OnboardingAppBarProps) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  return (
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 3,
        minHeight: layout.appBarHeight,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'custom.neutral.200',
        bgcolor: 'background.default',
      }}
    >
      {label ? (
        <Typography variant='subtitle2' sx={{ color: 'primary.main' }}>
          {label}
        </Typography>
      ) : (
        <Logo size='md' variant='symbol' />
      )}

      <Avatar
        component='button'
        aria-label='Settings'
        onClick={() => setIsSheetOpen(true)}
        sx={{
          width: layout.barAvatarSize,
          height: layout.barAvatarSize,
          p: 0,
          border: 0,
          flexShrink: 0,
          bgcolor: 'custom.neutral.400',
          color: 'custom.neutral.50',
          fontSize: 'body2.fontSize',
          cursor: 'pointer',
        }}
      >
        {initials ?? <Icon icon='user' fontSize={16} />}
      </Avatar>
      <AccountSheet
        isOpen={isSheetOpen}
        email={email}
        onClose={() => setIsSheetOpen(false)}
        onSignOut={onSignOut}
        onHelp={onHelp}
      />
    </Stack>
  );
};

OnboardingAppBar.displayName = 'OnboardingAppBar';
export default OnboardingAppBar;
